import { buildCustomPiiEntityNameMap } from "@frontend-shared/utils";
import type { PropsWithChildren, ReactNode } from "react";
import { memo, useMemo } from "react";

import { useListPiiCustomEntityLabels } from "@/api/pii/useCustomEntities";
import { PiiCustomEntityLabelsContext } from "@/contexts/PiiCustomEntityLabelsContext";
import { useActiveOrganizationUuid } from "@/hooks/use-active-organization-uuid";
import { usePiiCustomEntitiesFeatureEnabled } from "@/hooks/use-pii-custom-entities-feature-enabled";

// NOTE: NSAI-6787 — Security Analytics, chat messages and the Anonymization Lab
// all render entity types from the ACTIVE organization's pipeline, so one
// labels request for that organization covers every chip and summary. Asked
// only while the custom-entities flag is on (the endpoint 404s otherwise) and
// only inside an organization (a personal project has no custom entities).
const PiiCustomEntityLabelsProviderComponent = ({
  children,
}: PropsWithChildren): ReactNode => {
  const isPiiCustomEntitiesFeatureEnabled =
    usePiiCustomEntitiesFeatureEnabled();
  const organizationUuid = useActiveOrganizationUuid();
  const { data: labels } = useListPiiCustomEntityLabels({
    organizationUuid,
    isEnabled: isPiiCustomEntitiesFeatureEnabled,
  });

  const customEntityNames = useMemo(
    () => (labels ? buildCustomPiiEntityNameMap(labels) : null),
    [labels],
  );

  return (
    <PiiCustomEntityLabelsContext.Provider value={customEntityNames}>
      {children}
    </PiiCustomEntityLabelsContext.Provider>
  );
};

export const PiiCustomEntityLabelsProvider = memo(
  PiiCustomEntityLabelsProviderComponent,
);
